import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  MessageCircleMore,
  ChevronDown,
  User,
  Menu,
  X,
  Heart,
  LogOut,
  LayoutDashboard,
  Handshake,
  Crown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LOGO_URL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_EMAIL,
} from "@/lib/brand";

export default function Navbar({ onOpenAuth }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [contactOpen, setContactOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [isMember, setIsMember] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsMember(false);
      return;
    }

    if (user.role === "admin") {
      setIsMember(true);
      return;
    }

    api
      .get("/memberships/my")
      .then((r) => setIsMember(!!r.data.active))
      .catch(() => setIsMember(false));
  }, [user]);

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "G";

  /*
   * Search navigation helper.
   *
   * We intentionally use window.location.href instead of
   * React Router's Link for search filters.
   *
   * This makes sure that changing:
   *
   * /search?type=home+stay
   *
   * to:
   *
   * /search?type=resort
   *
   * reloads the search page and loads the correct results.
   */
  const goToSearch = (type) => {
    setMobileOpen(false);
    setMobileExploreOpen(false);
    setContactOpen(false);
    setUserMenu(false);

    window.location.href = `/search?type=${type}`;
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileExploreOpen(false);
  };

  const scrollToMembership = () => {
    setUserMenu(false);
    setMobileOpen(false);
    setMobileExploreOpen(false);

    if (window.location.pathname !== "/") {
      nav("/#membership");

      setTimeout(() => {
        document
          .getElementById("membership")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } else {
      document
        .getElementById("membership")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className="glass-header sticky top-0 z-50"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-between gap-6">
        {/* =========================
            LOGO
        ========================== */}
        <Link
          to="/"
          className="flex items-center"
          data-testid="navbar-logo"
        >
          <img
            src={LOGO_URL}
            alt="One Light Stays"
            className="h-12 md:h-14 w-auto"
          />
        </Link>

        {/* =========================
            DESKTOP NAVIGATION
        ========================== */}
        <nav className="hidden md:flex items-center gap-7">
          {/* Hotels */}
          <button
            type="button"
            onClick={() => goToSearch("hotel")}
            className="text-sm font-medium hover:text-stone-600 transition"
          >
            Hotels
          </button>

          {/* Villas */}
          <button
            type="button"
            onClick={() => goToSearch("villa")}
            className="text-sm font-medium hover:text-stone-600 transition"
          >
            Villas
          </button>

          {/* Camps */}
          <button
            type="button"
            onClick={() => goToSearch("camp")}
            className="text-sm font-medium hover:text-stone-600 transition"
          >
            Camps
          </button>

          {/* Explore Dropdown */}
          <div className="relative group">
            <button
              type="button"
              className="text-sm font-medium hover:text-stone-600 transition flex items-center gap-1"
            >
              Explore

              <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
            </button>

            <div className="absolute left-0 top-full mt-2 w-60 bg-white rounded-lg shadow-lg border border-stone-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              {/* Resorts */}
              <button
                type="button"
                onClick={() => goToSearch("resort")}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-stone-100"
              >
                Resorts
              </button>

              {/* Backpacker Hostels */}
              <button
                type="button"
                onClick={() => goToSearch("backpacker+hostel")}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-stone-100"
              >
                Backpacker Hostels
              </button>

              {/* Homestays */}
              <button
                type="button"
                onClick={() => goToSearch("home+stay")}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-stone-100"
              >
                Homestays
              </button>

              {/* Glamping */}
              <button
                type="button"
                onClick={() => goToSearch("glamping+%26+camp+site")}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-stone-100"
              >
                Glamping & Luxury Campsites
              </button>

              {/* Co-living */}
              <button
                type="button"
                onClick={() => goToSearch("co+-+living")}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-stone-100"
              >
                Co-living
              </button>

              {/* Long Stay */}
              <button
                type="button"
                onClick={() => goToSearch("long+stay+properties")}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-stone-100"
              >
                Long-Stay Properties
              </button>
            </div>
          </div>

          {/* Apartments in Goa */}
          <Link
            to="/apartments-in-goa"
            className="text-sm font-medium hover:text-stone-600 transition inline-flex items-center gap-1"
          >
            Apartments in Goa
          </Link>
        </nav>

        {/* =========================
            RIGHT SIDE ACTIONS
        ========================== */}
        <div className="flex items-center gap-2">
          {/* Become a Partner */}
          <button
            className="hidden lg:flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-full text-xs font-medium"
            data-testid="download-app"
          >
            <Handshake className="w-4 h-4" />

            <Link
              to="/become-a-partner"
              className="hover:text-stone-600 transition"
            >
              <span>Become a Partner</span>
            </Link>
          </button>

          {/* =========================
              USER MENU
          ========================== */}
          {user === undefined ? null : user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenu((v) => !v)}
                className="w-10 h-10 rounded-full bg-rose-200 text-stone-900 font-semibold text-sm flex items-center justify-center hover:bg-rose-300 transition"
                data-testid="user-menu-trigger"
              >
                {initials}
              </button>

              {userMenu && (
                <>
                  {/* Overlay */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenu(false)}
                  />

                  {/* User Dropdown */}
                  <div
                    className="absolute right-0 top-full mt-3 w-72 bg-white border border-stone-200 rounded-xl shadow-xl z-50 p-2"
                    data-testid="user-menu"
                  >
                    {/* User Information */}
                    <div className="flex items-center gap-3 p-3 border-b border-stone-100">
                      <div className="w-10 h-10 rounded-full bg-stone-900 text-white font-semibold text-sm flex items-center justify-center">
                        {initials}
                      </div>

                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {user.name}
                        </div>

                        <div className="text-xs text-stone-500 truncate">
                          {user.email}
                        </div>
                      </div>
                    </div>

                    {/* My Profile */}
                    <Link
                      to="/profile"
                      onClick={() => setUserMenu(false)}
                      className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm"
                      data-testid="menu-profile"
                    >
                      <User className="w-4 h-4" />
                      My Profile
                    </Link>

                    {/* Trips */}
                    <Link
                      to="/profile?tab=bookings"
                      onClick={() => setUserMenu(false)}
                      className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm"
                      data-testid="menu-bookings"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Trips
                    </Link>

                    {/* Wishlist */}
                    <Link
                      to="/profile?tab=wishlist"
                      onClick={() => setUserMenu(false)}
                      className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm"
                      data-testid="menu-wishlist"
                    >
                      <Heart className="w-4 h-4" />
                      Wishlist
                    </Link>

                    {/* Membership */}
                    {isMember ? (
                      <Link
                        to="/profile?tab=membership"
                        onClick={() => setUserMenu(false)}
                        className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm"
                        data-testid="menu-membership-dashboard"
                      >
                        <Crown className="w-4 h-4" />
                        Membership
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={scrollToMembership}
                        className="w-full text-left flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm"
                        data-testid="menu-buy-membership"
                      >
                        <Crown className="w-4 h-4" />
                        Buy Membership
                      </button>
                    )}

                    {/* Admin Panel */}
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenu(false)}
                        className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm border-t border-stone-100 mt-1 pt-3"
                        data-testid="menu-admin"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    )}

                    {/* Logout */}
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenu(false);
                        logout();
                        nav("/");
                      }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg text-sm border-t border-stone-100 mt-1 pt-3 text-stone-700"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Sign In */
            <button
              type="button"
              onClick={onOpenAuth}
              className="w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center hover:bg-stone-50"
              data-testid="navbar-signin"
            >
              <User className="w-4 h-4" />
            </button>
          )}

          {/* =========================
              GET IN TOUCH
          ========================== */}
          <div
            className="relative"
            onMouseLeave={() => setContactOpen(false)}
          >
            <button
              type="button"
              onMouseEnter={() => setContactOpen(true)}
              onClick={() => setContactOpen((v) => !v)}
              className="btn-primary text-sm !px-3 md:!px-5 whitespace-nowrap"
              data-testid="nav-contact-trigger"
            >
              <Phone className="w-3.5 h-3.5" />

              <span className="hidden sm:inline">
                Get in touch
              </span>

              <ChevronDown className="w-3.5 h-3.5 hidden sm:inline" />
            </button>

            {contactOpen && (
              <div
                className="absolute right-0 top-full pt-3 w-72 z-50"
                data-testid="contact-dropdown"
              >
                <div className="bg-white border border-stone-200 rounded-xl shadow-xl p-2">
                  {/* Call */}
                  <a
                    href={`tel:${SUPPORT_PHONE}`}
                    className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg"
                    data-testid="contact-call"
                  >
                    <div className="w-9 h-9 rounded-full bg-stone-900 text-white flex items-center justify-center">
                      <Phone className="w-4 h-4" />
                    </div>

                    <div>
                      <div className="text-sm font-medium">
                        Call us
                      </div>

                      <div className="text-xs text-stone-500">
                        {SUPPORT_PHONE_DISPLAY}
                      </div>
                    </div>
                  </a>

                  {/* WhatsApp */}
                  <a
                    href={`https://wa.me/${SUPPORT_PHONE}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg"
                    data-testid="contact-whatsapp"
                  >
                    <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                      <MessageCircleMore className="w-4 h-4" />
                    </div>

                    <div>
                      <div className="text-sm font-medium">
                        WhatsApp
                      </div>

                      <div className="text-xs text-stone-500">
                        Instant inquiry
                      </div>
                    </div>
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg"
                    data-testid="contact-email"
                  >
                    <div className="w-9 h-9 rounded-full bg-stone-100 text-stone-900 flex items-center justify-center">
                      <Mail className="w-4 h-4" />
                    </div>

                    <div>
                      <div className="text-sm font-medium">
                        Email
                      </div>

                      <div className="text-xs text-stone-500">
                        {SUPPORT_EMAIL}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* =========================
              MOBILE MENU BUTTON
          ========================== */}
          <button
            type="button"
            className="md:hidden p-2"
            onClick={() => {
              setMobileOpen((prev) => !prev);

              // Reset Explore whenever the main mobile menu is opened/closed.
              setMobileExploreOpen(false);
            }}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle mobile menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* =========================
          MOBILE NAVIGATION
      ========================== */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-stone-200 bg-white"
          data-testid="mobile-nav"
        >
          {/* Hotels */}
          <button
            type="button"
            onClick={() => goToSearch("hotel")}
            className="flex items-center justify-between w-full text-left p-4 border-b hover:bg-stone-50"
          >
            <span>Hotels</span>
          </button>

          {/* Villas */}
          <button
            type="button"
            onClick={() => goToSearch("villa")}
            className="flex items-center justify-between w-full text-left p-4 border-b hover:bg-stone-50"
          >
            <span>Villas</span>
          </button>

          {/* Camps */}
          <button
            type="button"
            onClick={() => goToSearch("camp")}
            className="flex items-center justify-between w-full text-left p-4 border-b hover:bg-stone-50"
          >
            <span>Camps</span>
          </button>

          {/* =========================
              EXPLORE ACCORDION
          ========================== */}
          <div className="border-b">
            {/* Explore Header */}
            <button
              type="button"
              onClick={() =>
                setMobileExploreOpen((prev) => !prev)
              }
              className="flex items-center justify-between w-full p-4 text-left hover:bg-stone-50"
              aria-expanded={mobileExploreOpen}
            >
              <span>Explore</span>

              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  mobileExploreOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Explore Submenu */}
            {mobileExploreOpen && (
              <div className="bg-stone-50 border-t border-stone-100">
                {/* Resorts */}
                <button
                  type="button"
                  onClick={() => goToSearch("resort")}
                  className="block w-full text-left px-7 py-3 text-sm hover:bg-stone-100"
                >
                  Resorts
                </button>

                {/* Backpacker Hostels */}
                <button
                  type="button"
                  onClick={() =>
                    goToSearch("backpacker+hostel")
                  }
                  className="block w-full text-left px-7 py-3 text-sm hover:bg-stone-100"
                >
                  Backpacker Hostels
                </button>

                {/* Homestays */}
                <button
                  type="button"
                  onClick={() => goToSearch("home+stay")}
                  className="block w-full text-left px-7 py-3 text-sm hover:bg-stone-100"
                >
                  Homestays
                </button>

                {/* Glamping & Luxury Campsites */}
                <button
                  type="button"
                  onClick={() =>
                    goToSearch("glamping+%26+camp+site")
                  }
                  className="block w-full text-left px-7 py-3 text-sm hover:bg-stone-100"
                >
                  Glamping & Luxury Campsites
                </button>

                {/* Co-living */}
                <button
                  type="button"
                  onClick={() =>
                    goToSearch("co+-+living")
                  }
                  className="block w-full text-left px-7 py-3 text-sm hover:bg-stone-100"
                >
                  Co-living
                </button>

                {/* Long-Stay Properties */}
                <button
                  type="button"
                  onClick={() =>
                    goToSearch("long+stay+properties")
                  }
                  className="block w-full text-left px-7 py-3 text-sm hover:bg-stone-100"
                >
                  Long-Stay Properties
                </button>
              </div>
            )}
          </div>

          {/* Apartments in Goa */}
          <Link
            to="/apartments-in-goa"
            className="block p-4 border-b hover:bg-stone-50"
            onClick={closeMobileMenu}
          >
            Apartments in Goa
          </Link>

          {/* Become a Partner */}
          <Link
            to="/become-a-partner"
            className="block p-4 border-b hover:bg-stone-50"
            onClick={closeMobileMenu}
          >
            Become a Partner
          </Link>

          {/* =========================
              MOBILE USER MENU
          ========================== */}
          {user ? (
            <>
              {/* My Profile */}
              <Link
                to="/profile"
                className="block p-4 border-b hover:bg-stone-50"
                onClick={closeMobileMenu}
              >
                My Profile
              </Link>

              {/* Trips */}
              <Link
                to="/profile?tab=bookings"
                className="block p-4 border-b hover:bg-stone-50"
                onClick={closeMobileMenu}
              >
                Trips
              </Link>

              {/* Wishlist */}
              <Link
                to="/profile?tab=wishlist"
                className="block p-4 border-b hover:bg-stone-50"
                onClick={closeMobileMenu}
              >
                Wishlist
              </Link>

              {/* Membership */}
              {isMember ? (
                <Link
                  to="/profile?tab=membership"
                  className="block p-4 border-b hover:bg-stone-50"
                  onClick={closeMobileMenu}
                >
                  Membership
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={scrollToMembership}
                  className="block w-full text-left p-4 border-b hover:bg-stone-50"
                >
                  Buy Membership
                </button>
              )}

              {/* Admin Panel */}
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="block p-4 border-b hover:bg-stone-50"
                  onClick={closeMobileMenu}
                >
                  Admin Panel
                </Link>
              )}

              {/* Logout */}
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  setMobileExploreOpen(false);
                  logout();
                  nav("/");
                }}
                className="block w-full text-left p-4 hover:bg-stone-50"
              >
                Logout
              </button>
            </>
          ) : (
            /* Sign In */
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                setMobileExploreOpen(false);
                onOpenAuth();
              }}
              className="block w-full text-left p-4 hover:bg-stone-50"
            >
              Sign in
            </button>
          )}
        </div>
      )}
    </header>
  );
}
